/**
 * Employee Voting Service Layer
 * Handles all Firestore operations for employee of the month voting system
 *
 * Collections:
 * - voting_periods: Defines when voting is open/closed
 * - votes: Individual votes cast by employees
 * - voting_results: Aggregated results after voting closes
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { VotingPeriod, Vote, VotingResults } from '../types/schema';
import { updateVotingStatsForPeriod } from './voting-stats.service';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { stripUndefinedDeep } from '@/utils/firestore';
import { getLocaleSync } from '@/utils/locale';

const VOTING_PERIODS_COLLECTION = 'voting_periods';
const VOTES_COLLECTION = 'votes';
const VOTING_RESULTS_COLLECTION = 'voting_results';
const USERS_COLLECTION = 'users';

// ============================================================================
// VOTING PERIOD MANAGEMENT
// ============================================================================

/**
 * Create a new voting period
 * Opens voting for 3 days towards the end of the month
 */
export async function createVotingPeriod(
  month: string, // "2026-02"
  year: number,
  startDate: Date,
  endDate: Date,
  tenantId?: string
): Promise<string> {
  // Always stamp the canonical tenantId so the doc is isolation-ready.
  const period: Omit<VotingPeriod, 'id'> = {
    tenantId: tenantId ?? getTenantIdSync(),
    month,
    year,
    title: `Employee of the Month - ${new Date(year, parseInt(month.split('-')[1]) - 1).toLocaleDateString(getLocaleSync(), { month: 'long', year: 'numeric' })}`,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    status: new Date() >= startDate ? 'active' : 'upcoming',
    createdAt: new Date().toISOString(),
  };

  // Strip undefined (`tenantId` is optional) — Firestore rejects any document
  // carrying an `undefined` value.
  const docRef = await addDoc(
    collection(db, VOTING_PERIODS_COLLECTION),
    stripUndefinedDeep(period)
  );
  return docRef.id;
}

/**
 * Get the current active voting period
 */
export async function getCurrentVotingPeriod(tenantId?: string): Promise<VotingPeriod | null> {
  // Always tenant-filter so this is valid under a future strict isolation
  // rule. Resolves to canonical when the caller omits it.
  const tid = tenantId ?? getTenantIdSync();
  const q = query(
    collection(db, VOTING_PERIODS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tid)),
    where('status', '==', 'active'),
    orderBy('startDate', 'desc'),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data()
  } as VotingPeriod;
}

/**
 * Get upcoming voting period
 */
export async function getUpcomingVotingPeriod(tenantId?: string): Promise<VotingPeriod | null> {
  const tid = tenantId ?? getTenantIdSync();
  const q = query(
    collection(db, VOTING_PERIODS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tid)),
    where('status', '==', 'upcoming'),
    orderBy('startDate', 'asc'),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data()
  } as VotingPeriod;
}

/**
 * Close a voting period and mark as closed
 */
export async function closeVotingPeriod(periodId: string): Promise<void> {
  const docRef = doc(db, VOTING_PERIODS_COLLECTION, periodId);

  await updateDoc(docRef, {
    status: 'closed',
    closedAt: new Date().toISOString()
  });
}

/**
 * Get all voting periods (admin function)
 */
export async function getAllVotingPeriods(tenantId?: string): Promise<VotingPeriod[]> {
  const tid = tenantId ?? getTenantIdSync();
  const q = query(
    collection(db, VOTING_PERIODS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tid)),
    orderBy('startDate', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as VotingPeriod));
}

// ============================================================================
// VOTING OPERATIONS
// ============================================================================

/**
 * Cast a vote for employee of the month
 * Prevents self-voting
 */
export async function castVote(
  votingPeriodId: string,
  voterId: string,
  voterName: string,
  nomineeId: string,
  nomineeName: string,
  reason?: string,
  tenantId?: string
): Promise<string> {
  // Prevent self-voting
  if (voterId === nomineeId) {
    throw new Error('You cannot vote for yourself');
  }

  // Check if user has already voted in this period
  const existingVote = await getUserVoteForPeriod(votingPeriodId, voterId);
  if (existingVote) {
    throw new Error('You have already voted in this period');
  }

  // Get voting period to extract month/year
  const periodDoc = await getDoc(doc(db, VOTING_PERIODS_COLLECTION, votingPeriodId));
  if (!periodDoc.exists()) {
    throw new Error('Voting period not found');
  }

  const period = periodDoc.data() as VotingPeriod;

  const vote: Omit<Vote, 'id'> = {
    tenantId: tenantId ?? getTenantIdSync(),
    votingPeriodId,
    month: period.month,
    year: period.year,
    voterId,
    voterName,
    nomineeId,
    nomineeName,
    reason,
    createdAt: new Date().toISOString()
  };

  // Strip undefined fields — `reason` is optional (and `tenantId` may be
  // absent), and Firestore rejects any document carrying an `undefined`
  // value ("Unsupported field value: undefined"), which surfaced to voters
  // as an error on every reasonless vote.
  const docRef = await addDoc(collection(db, VOTES_COLLECTION), stripUndefinedDeep(vote));
  return docRef.id;
}

/**
 * Check if a user has voted in a specific period
 */
export async function getUserVoteForPeriod(
  votingPeriodId: string,
  voterId: string
): Promise<Vote | null> {
  const q = query(
    collection(db, VOTES_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('votingPeriodId', '==', votingPeriodId),
    where('voterId', '==', voterId),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data()
  } as Vote;
}

/**
 * Check if user has voted (boolean check)
 */
export async function hasUserVoted(
  votingPeriodId: string,
  voterId: string
): Promise<boolean> {
  const vote = await getUserVoteForPeriod(votingPeriodId, voterId);
  return vote !== null;
}

/**
 * Get all votes for a specific voting period
 */
export async function getVotesForPeriod(votingPeriodId: string): Promise<Vote[]> {
  const q = query(
    collection(db, VOTES_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('votingPeriodId', '==', votingPeriodId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Vote));
}

// ============================================================================
// RESULTS CALCULATION
// ============================================================================

/**
 * Calculate voting results for a period.
 *
 * Assembles:
 * - results[]: ranked list of all nominees with vote counts
 * - winnerReasons[]: anonymous reasons for the winner (voter identity stripped)
 * - runnersUp[]: everyone else who got votes (no reasons, no voter names)
 *
 * Also closes the period and marks it `announced: true` so the reveal UI knows
 * to show it to all users.
 */
export async function calculateVotingResults(
  votingPeriodId: string,
  totalEligibleVoters: number,
  closeReason: 'scheduled' | 'everyone_voted' | 'manual' = 'manual'
): Promise<VotingResults> {
  const votes = await getVotesForPeriod(votingPeriodId);

  const periodDoc = await getDoc(doc(db, VOTING_PERIODS_COLLECTION, votingPeriodId));
  if (!periodDoc.exists()) {
    throw new Error('Voting period not found');
  }
  const period = periodDoc.data() as VotingPeriod;

  // Count votes per nominee and collect reasons per nominee.
  // Reasons are collected keyed by nomineeId so we can pull the winner's
  // reasons without ever leaking voter identity downstream.
  const voteCounts = new Map<
    string,
    { userId: string; userName: string; userRole?: string; count: number }
  >();
  const reasonsByNominee = new Map<string, string[]>();

  votes.forEach((vote) => {
    const existing = voteCounts.get(vote.nomineeId);
    if (existing) {
      existing.count++;
    } else {
      voteCounts.set(vote.nomineeId, {
        userId: vote.nomineeId,
        userName: vote.nomineeName,
        userRole: vote.nomineeRole,
        count: 1,
      });
    }
    const reason = (vote.reason || '').trim();
    if (reason) {
      const bucket = reasonsByNominee.get(vote.nomineeId) || [];
      bucket.push(reason);
      reasonsByNominee.set(vote.nomineeId, bucket);
    }
  });

  const totalVotes = votes.length;

  const sortedResults = Array.from(voteCounts.values())
    .sort((a, b) => b.count - a.count)
    .map((result, index) => ({
      userId: result.userId,
      userName: result.userName,
      userRole: result.userRole,
      voteCount: result.count,
      percentage: totalVotes > 0 ? Math.round((result.count / totalVotes) * 100) : 0,
      rank: index + 1,
    }));

  const winner = sortedResults[0];
  const participationRate =
    totalEligibleVoters > 0
      ? Math.round((totalVotes / totalEligibleVoters) * 100)
      : 0;

  // Winner reasons — shuffled so ordering reveals nothing about who voted when.
  const winnerReasons = winner
    ? shuffleArray(reasonsByNominee.get(winner.userId) || [])
    : [];

  // Runners-up — everyone who got votes except the winner, sorted by count desc.
  const runnersUp = sortedResults
    .filter((r) => r.userId !== (winner?.userId || ''))
    .map((r) => ({
      userId: r.userId,
      userName: r.userName,
      userRole: r.userRole,
      voteCount: r.voteCount,
    }));

  const now = new Date().toISOString();

  // closedAt drives every "most recent winner" sort (useCurrentEmployeeOfMonth,
  // useVotingAnnouncement, getLatestAnnouncedResult, getAllVotingResults). For a
  // SCHEDULED close of an already-expired period — including the self-healing
  // recovery sweep, which can fire weeks after the period actually ended — anchor
  // closedAt to the period's endDate (when it SHOULD have closed) instead of
  // `now`. Otherwise a long-stuck OLD period closes with closedAt=today and
  // leapfrogs the genuinely-current winner, surfacing the wrong Employee of the
  // Month. Early ('everyone_voted') + manual closes keep `now` — they really did
  // close at that moment, before endDate.
  const closedAtValue =
    period.closedAt ||
    (closeReason === 'scheduled' && period.endDate ? period.endDate : now);

  const results: Omit<VotingResults, 'id'> = {
    votingPeriodId,
    // Propagate the period's tenantId; fall back to canonical for legacy
    // periods that pre-date tenant stamping so the result is isolation-ready.
    tenantId: period.tenantId ?? getTenantIdSync(),
    month: period.month,
    year: period.year,
    title: period.title,
    results: sortedResults,
    totalVotes,
    totalEligibleVoters,
    participationRate,
    winnerId: winner?.userId || '',
    winnerName: winner?.userName || '',
    winnerRole: winner?.userRole,
    winnerReasons,
    runnersUp,
    calculatedAt: now,
    closedAt: closedAtValue,
  };

  // Save/overwrite results. Document ID = votingPeriodId so we can reliably
  // look it up; setDoc with merge avoids the old "try update then fall back to
  // addDoc" pattern which created duplicates.
  //
  // Strip undefined fields before writing — `tenantId` (period may have none),
  // `winnerRole`, and the per-entry `userRole` in `results[]`/`runnersUp[]` are
  // all optional and land as `undefined` when a nominee has no role. Firestore
  // rejects any document carrying `undefined` ("Unsupported field value:
  // undefined"), which was failing the recovery sweep that closes expired
  // periods.
  const resultsRef = doc(db, VOTING_RESULTS_COLLECTION, votingPeriodId);
  await setDoc(resultsRef, stripUndefinedDeep(results), { merge: true });

  // Update voting period with winner info + announcement flag.
  await updateDoc(doc(db, VOTING_PERIODS_COLLECTION, votingPeriodId), {
    winnerId: winner?.userId || '',
    winnerName: winner?.userName || '',
    totalVotes,
    participationRate,
    status: 'closed',
    closedAt: closedAtValue,
    announced: true,
    announcedAt: now,
    closeReason,
  });

  // Update voting stats for all nominees (leaderboard, streaks, etc.).
  // This is what populates the Hall of Fame — keep it in lock-step with the
  // server-side mirror in functions/src/index.ts (calculateAndCloseVotingPeriod).
  const fullResults: VotingResults = { id: votingPeriodId, ...results };
  try {
    await updateVotingStatsForPeriod(votingPeriodId, fullResults);
    // Stamp the idempotency gate so recovery sweeps skip this period.
    await setDoc(resultsRef, { statsAppliedAt: now }, { merge: true });
    fullResults.statsAppliedAt = now;
  } catch (err) {
    console.error('[voting] Failed to update stats after calculating results:', err);
    // Non-fatal — results are still saved. The next recovery sweep
    // (recoverVotingState) will re-attempt the stats update.
  }

  return fullResults;
}

/**
 * Apply the Hall-of-Fame stats for an already-calculated result, if they
 * haven't been applied yet. Idempotent at two layers:
 *   1. The `statsAppliedAt` gate on the result doc (cheap skip).
 *   2. Per-user `processedPeriodIds` inside updateVotingStatsForPeriod
 *      (correctness even if the gate write was lost mid-flight).
 *
 * Returns true if stats were applied on this call.
 */
async function applyStatsForResultIfNeeded(results: VotingResults): Promise<boolean> {
  if (!results.votingPeriodId) return false;
  if (results.statsAppliedAt) return false; // already applied — cheap skip
  if (!results.results || results.results.length === 0) return false; // nothing to apply

  await updateVotingStatsForPeriod(results.votingPeriodId, results);
  const now = new Date().toISOString();
  await setDoc(
    doc(db, VOTING_RESULTS_COLLECTION, results.votingPeriodId),
    { statsAppliedAt: now },
    { merge: true }
  );
  return true;
}

// Run-once-per-load guard so multiple component mounts don't each trigger a
// recovery sweep. Reset on full page reload (which is the right cadence).
let recoveryInFlight: Promise<{ closedExpired: number; statsBackfilled: number }> | null = null;

/**
 * Self-healing recovery for the voting system. Safe to call fire-and-forget
 * on app load. Two jobs:
 *
 *   1. Close any expired-but-still-active period (the scheduled Cloud Function
 *      may not have fired). Closing recalculates results AND applies stats.
 *   2. Backfill Hall-of-Fame stats for any closed period whose results exist
 *      but were never folded into voting_stats — this recovers periods closed
 *      by the old server path that didn't update stats.
 *
 * Everything is idempotent (see applyStatsForResultIfNeeded + the per-user
 * processedPeriodIds guard), so repeated calls converge without double-counting.
 *
 * @param force  bypass the once-per-load guard (used by the manual button).
 */
export async function recoverVotingState(
  tenantId?: string,
  opts: { force?: boolean } = {}
): Promise<{ closedExpired: number; statsBackfilled: number }> {
  if (recoveryInFlight && !opts.force) return recoveryInFlight;

  const run = (async () => {
    let closedExpired = 0;
    let statsBackfilled = 0;
    const nowIso = new Date().toISOString();
    // Always tenant-filter (resolve canonical when omitted) so these reads
    // stay valid under a future strict isolation rule.
    const tid = tenantId ?? getTenantIdSync();

    // ── 1. Close expired-but-active periods ────────────────────────────────
    try {
      const periodsCol = collection(db, VOTING_PERIODS_COLLECTION);
      const activeQ = query(
        periodsCol,
        where('tenantId', 'in', tenantIdReadUnion(tid)),
        where('status', '==', 'active'),
        orderBy('startDate', 'desc')
      );
      const activeSnap = await getDocs(activeQ);

      for (const d of activeSnap.docs) {
        const period = d.data() as VotingPeriod;
        if (period.endDate && period.endDate <= nowIso) {
          const eligible = await getEligibleVoterCount(tenantId || period.tenantId);
          await calculateVotingResults(d.id, eligible, 'scheduled');
          closedExpired++;
        }
      }
    } catch (err) {
      console.error('[voting] recovery: failed closing expired periods:', err);
    }

    // ── 2. Backfill stats for closed results that never got applied ─────────
    try {
      const resultsCol = collection(db, VOTING_RESULTS_COLLECTION);
      const resultsQ = query(
        resultsCol,
        where('tenantId', 'in', tenantIdReadUnion(tid)),
        orderBy('closedAt', 'desc'),
        limit(12)
      );
      const resultsSnap = await getDocs(resultsQ);

      for (const d of resultsSnap.docs) {
        const results = { id: d.id, ...d.data() } as VotingResults;

        // Self-heal a late-closed period's closedAt. A period that closed weeks
        // late (stuck active → recovery/cron close) was stamped closedAt=today,
        // which lands it in a LATER calendar month than the period it belongs to
        // and makes an OLD result sort as the freshest — surfacing the wrong
        // "current Employee of the Month". Re-anchor closedAt to the end of the
        // period's own month so it sorts chronologically. Idempotent: once fixed,
        // closedAt's month == results.month, so the guard never re-fires.
        try {
          if (
            results.month &&
            typeof results.closedAt === 'string' &&
            results.closedAt.slice(0, 7) > results.month
          ) {
            const [yy, mm] = results.month.split('-').map(Number);
            if (yy && mm) {
              const lastDay = new Date(yy, mm, 0).getDate();
              const healed = `${results.month}-${String(lastDay).padStart(2, '0')}T23:59:59.999Z`;
              await setDoc(
                doc(db, VOTING_RESULTS_COLLECTION, d.id),
                { closedAt: healed },
                { merge: true }
              );
              results.closedAt = healed;
            }
          }
        } catch (err) {
          console.error(`[voting] recovery: failed healing closedAt for ${d.id}:`, err);
        }

        if (!results.winnerId || results.statsAppliedAt) continue;
        try {
          const applied = await applyStatsForResultIfNeeded(results);
          if (applied) statsBackfilled++;
        } catch (err) {
          console.error(`[voting] recovery: failed backfilling stats for ${d.id}:`, err);
        }
      }
    } catch (err) {
      console.error('[voting] recovery: failed backfilling stats:', err);
    }

    return { closedExpired, statsBackfilled };
  })();

  if (!opts.force) recoveryInFlight = run;
  try {
    return await run;
  } finally {
    // Allow a fresh sweep on the next explicit/forced call, but keep the
    // memoised promise for concurrent mounts within the same load.
    if (opts.force) recoveryInFlight = null;
  }
}

/**
 * Fisher-Yates shuffle — used to randomise the order of winner reasons so
 * readers can't infer anything about voting order.
 */
function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Get voting results for a period
 */
export async function getVotingResults(votingPeriodId: string): Promise<VotingResults | null> {
  const docRef = doc(db, VOTING_RESULTS_COLLECTION, votingPeriodId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data()
  } as VotingResults;
}

/**
 * Get all voting results (admin function)
 */
export async function getAllVotingResults(tenantId?: string): Promise<VotingResults[]> {
  const tid = tenantId ?? getTenantIdSync();
  const q = query(
    collection(db, VOTING_RESULTS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tid)),
    orderBy('closedAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as VotingResults));
}

// ============================================================================
// ELIGIBLE VOTERS & EARLY CLOSE
// ============================================================================

/**
 * Count eligible voters for this tenant.
 * Everyone counts — office, sales, ops, tech — except archived / inactive users.
 * (Users can't vote for themselves, so "eligible voters" == "eligible nominees".)
 */
export async function getEligibleVoterCount(tenantId?: string): Promise<number> {
  const usersRef = collection(db, USERS_COLLECTION);
  const q = tenantId
    ? query(usersRef, where('tenantId', 'in', tenantIdReadUnion(tenantId)))
    : query(usersRef);
  const snapshot = await getDocs(q);

  return snapshot.docs.filter((d) => {
    const data = d.data() as { archived?: boolean; deleted?: boolean; status?: string };
    if (data.archived === true) return false;
    if (data.deleted === true) return false;
    // Legacy status field: 'Active' | 'Inactive'
    if (data.status === 'Inactive') return false;
    return true;
  }).length;
}

/**
 * If everyone eligible has voted, close the period early and calculate results.
 * Call this fire-and-forget after a successful castVote() — if nothing
 * qualifies, it quietly no-ops.
 *
 * Returns the calculated results (or null if nothing was closed).
 */
export async function maybeCloseEarlyIfAllVoted(
  votingPeriodId: string,
  tenantId?: string
): Promise<VotingResults | null> {
  const periodDoc = await getDoc(doc(db, VOTING_PERIODS_COLLECTION, votingPeriodId));
  if (!periodDoc.exists()) return null;
  const period = periodDoc.data() as VotingPeriod;
  if (period.status !== 'active') return null;

  const [votes, eligibleCount] = await Promise.all([
    getVotesForPeriod(votingPeriodId),
    getEligibleVoterCount(tenantId || period.tenantId),
  ]);

  // "Everyone voted" — allow the count to be equal OR exceed eligible
  // (someone voting, then being archived, shouldn't block the close).
  if (eligibleCount > 0 && votes.length >= eligibleCount) {
    return await calculateVotingResults(votingPeriodId, eligibleCount, 'everyone_voted');
  }
  return null;
}

/**
 * Get the most recent closed + announced voting result for the tenant.
 * Used by the announcement modal to know if there's anything to reveal.
 */
export async function getLatestAnnouncedResult(
  tenantId?: string
): Promise<VotingResults | null> {
  const tid = tenantId ?? getTenantIdSync();
  const q = query(
    collection(db, VOTING_RESULTS_COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(tid)),
    orderBy('closedAt', 'desc'),
    limit(5)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  // Return the newest result that has a non-empty winnerId (skip any
  // half-written docs). Cap at 5 to keep the read cheap.
  for (const d of snapshot.docs) {
    const data = { id: d.id, ...d.data() } as VotingResults;
    if (data.winnerId && data.winnerName) {
      return data;
    }
  }
  return null;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if voting is currently open
 */
export async function isVotingOpen(tenantId?: string): Promise<boolean> {
  const period = await getCurrentVotingPeriod(tenantId);
  return period !== null;
}

/**
 * Get days remaining in voting period
 */
export function getDaysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Get hours remaining in voting period
 */
export function getHoursRemaining(endDate: string): number {
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
  return Math.max(0, diffHours);
}
